import { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";

import { SnackbarNotification } from "../utils/Notifications";

import "./css/edit.css";

import { updateDoubt } from "../../functions/server-requests/doubt";
import { updateAnswer } from "../../functions/server-requests/answer";
import { updateComment } from "../../functions/server-requests/comment";
import useAuth from "../../hooks/useAuth";
import dateSlicer from "../../functions/dateSlicer";

const Edit = ({ openEdit, setOpenEdit, data }) => {
  const { auth } = useAuth();
  const [contentData, setContentData] = useState(data.content);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [message, setMessage] = useState("");
  const [isSubmitting, setSubmitting] = useState(false);

  const validationSchemaDoubt = yup.object({
    title: yup
      .string()
      .required('Required')
      .min(4, 'Must be at least 4 characters')
      .max(256, 'Must be at most 20 or 256 characters'),
    body: yup
      .string()
      .required('Required')
      .min(8, 'Must be at least 8 characters')
      .max(2048, 'Must be at most 2048 characters')
  });

  const validationSchemaAnsComm = yup.object({
    body: yup
      .string()
      .required('Required')
      .min(2, 'Must be at least 2 characters')
      .max(2048, 'Must be at most 2048 characters')
  });

  const handleDoubtUpdate = async (values) => {
    setSubmitting(true);

    const updateDoubtRes = await updateDoubt({
      doubtId: data.doubtId,
      askedById: auth.userId,
      title: values.title.trim(),
      desc: values.body.trim()
    });

    if (updateDoubtRes.status === 201) {
      setSeverity("success");

      setContentData({
        ...contentData,
        title: updateDoubtRes.data.title,
        body: updateDoubtRes.data.desc
      });

      data.updateableData.setDoubtDetails({
        ...data.updateableData.doubtDetails,
        title: updateDoubtRes.data.title,
        desc: updateDoubtRes.data.desc
      });
      data.updateableData.setUpdateableUpdatedOn(updateDoubtRes.data.updatedOn);

      setOpenEdit(false);
    }
    else {
      setSeverity("error");
    }

    setMessage(updateDoubtRes.data.message);
    setOpenSnackbar(true);
    setSubmitting(false);
  }

  const handleAnswerUpdate = async (values) => {
    setSubmitting(true);

    const updateAnswerRes = await updateAnswer({
      answerId: data.answerId,
      answeredById: auth.userId,
      answerBody: values.body.trim()
    });

    if (updateAnswerRes.status === 201) {
      setSeverity("success");

      setContentData({
        ...contentData,
        answerBody: updateAnswerRes.data.answerBody
      });

      data.updateableData.setUpdateableAnswerBody(updateAnswerRes.data.answerBody);
      data.updateableData.setUpdateableUpdatedOn(updateAnswerRes.data.updatedOn);

      setOpenEdit(false);
    }
    else {
      setSeverity("error");
    }

    setMessage(updateAnswerRes.data.message);
    setOpenSnackbar(true);
    setSubmitting(false);
  }

  const handleCommentUpdate = async (values) => {
    setSubmitting(true);

    const updateCommentRes = await updateComment({
      commentById: auth.userId,
      commentFor: data.commentFor,
      commentForId: data.commentForId,
      commentId: data.commentId,
      commentBody: values.body.trim()
    });

    if (updateCommentRes.status === 201) {
      setSeverity("success");
      setContentData({
        ...contentData,
        commentBody: updateCommentRes.data.commentBody
      });

      data.updateableData.setUpdateableCommentBody(updateCommentRes.data.commentBody);
      data.updateableData.setUpdateableUpdatedOn(dateSlicer(updateCommentRes.data.updatedOn));

      setOpenEdit(false);
    }
    else {
      setSeverity("error");
    }

    setMessage(updateCommentRes.data.message);
    setOpenSnackbar(true);
    setSubmitting(false);
  }

  const handleSubmit = (values) => {
    if (data.contentOf === "doubt") {
      handleDoubtUpdate(values);
    }
    else if (data.contentOf === "answer") {
      handleAnswerUpdate(values);
    }
    else if (data.contentOf === "comment") {
      handleCommentUpdate(values);
    }
  }

  const formik = useFormik({
    initialValues:
      data.contentOf === "doubt"
        ? {
          title: contentData.title,
          body: contentData.body
        }
        : {
          body: contentData.body
        },
    validationSchema:
      data.contentOf === "doubt"
        ? validationSchemaDoubt
        : validationSchemaAnsComm,
    onSubmit: async (values) => {
      handleSubmit(values);
    }
  });

  return (
    <>
      <Dialog
        fullWidth
        open={openEdit}
        onClose={() => setOpenEdit(false)}
      >
        <DialogTitle>
          <span className="title m-title">
            Edit {data.contentOf}
          </span>
        </DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            {data.contentOf === "doubt" &&
              <TextField
                fullWidth
                variant="standard"
                sx={{ mb: "16px" }}
                type="text"
                name="title"
                label="Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                disabled={isSubmitting}
              />
            }

            <TextField
              fullWidth
              multiline
              minRows={4}
              variant="standard"
              type="text"
              name="body"
              label={
                data.contentOf === "doubt"
                  ? "Description"
                  : data.contentOf === "answer"
                    ? "Answer"
                    : "Comment"
              }
              value={formik.values.body}
              onChange={formik.handleChange}
              error={formik.touched.body && Boolean(formik.errors.body)}
              helperText={formik.touched.body && formik.errors.body}
              disabled={isSubmitting}
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenEdit(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              type="submit"
              disabled={
                isSubmitting || (
                data.contentOf !== "doubt"
                  ? (formik.values.body.trim().length < 2)
                  : (
                    formik.values.title.trim().length < 4 ||
                    formik.values.body.trim().length < 8
                  )
                )
              }
            >
              {!isSubmitting
                ? "Update"
                : "Updating ..."
              }
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <SnackbarNotification
        openSnackbar={openSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        severity={severity}
        message={message}
      />
    </>
  );
}

export default Edit;